/**
 * Branch card component
 */
import { Branch } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ProtectedAction } from '@/components/shared/ProtectedAction';
import { canManageBranch } from '@/lib/permissions';

interface BranchCardProps {
  branch: Branch;
  onEdit?: (branchId: string) => void;
  onDelete?: (branchId: string) => void;
}

export function BranchCard({ branch, onEdit, onDelete }: BranchCardProps) {
  const isOpenNow = () => {
    const now = new Date();
    const day = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as keyof typeof branch.schedule;
    const daySchedule = branch.schedule[day];

    if (!daySchedule.isOpen) return false;

    const currentTime = now.toTimeString().slice(0, 5);
    return currentTime >= (daySchedule.open || '') && currentTime <= (daySchedule.close || '');
  };

  return (
    <Card className="p-0 hover:shadow-lg transition-shadow overflow-hidden">
      {/* Branch Photo */}
      <div className="relative h-48 ">
        {branch.photo ? (
          <img
            src={branch.photo}
            alt={branch.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <span className="text-6xl">📍</span>
          </div>
        )}
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium shadow-lg ${
            isOpenNow()
              ? 'bg-success/100 text-white'
              : 'bg-surface-20 text-ink'
          }`}>
            {isOpenNow() ? '● Abierto' : 'Cerrado'}
          </span>
        </div>
      </div>

      {/* Branch Info */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-ink mb-3">{branch.name}</h3>

      <div className="space-y-2 text-sm text-ink-muted mb-4">
        <div className="flex items-start gap-2">
          <span>📍</span>
          <div>
            <p>{branch.address}</p>
            <p>{branch.city}, {branch.province} {branch.postalCode}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span>📞</span>
          <span>{branch.phone}</span>
        </div>
        {branch.email && (
          <div className="flex items-center gap-2">
            <span>📧</span>
            <span className="truncate">{branch.email}</span>
          </div>
        )}
      </div>

        <ProtectedAction requiredPermission={(claims) => canManageBranch(claims, branch.franchiseId)}>
          <div className="flex gap-2 pt-4 border-t border-hairline">
            {onEdit && (
              <Button size="sm" variant="ghost" onClick={() => onEdit(branch.branchId)} className="flex-1">
                ✏️ Editar
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="danger" onClick={() => onDelete(branch.branchId)} className="flex-1">
                🗑️ Eliminar
              </Button>
            )}
          </div>
        </ProtectedAction>
      </div>
    </Card>
  );
}
